import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { X, Loader2, ExternalLink, MapPin, Building2, History } from 'lucide-react';
import { pushRecentSite, getRecentSites } from '../../lib/recentSites';
import SARButton from './SARButton';
import SiteProjectsPanel from './SiteProjectsPanel';
import SiteStatePanel from './SiteStatePanel';

const relativeTime = (ts) => {
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return 'hace unos segundos';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `hace ${m} min`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `hace ${h} h`;
  }
  const days = Math.floor(diff / 86400);
  if (days === 1) return 'ayer';
  if (days < 7) return `hace ${days} días`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `hace ${w} sem.`;
  }
  if (days < 365) {
    const mo = Math.floor(days / 30);
    return `hace ${mo} ${mo === 1 ? 'mes' : 'meses'}`;
  }
  const y = Math.floor(days / 365);
  return `hace ${y} ${y === 1 ? 'año' : 'años'}`;
};

const HIDDEN_KEYS = new Set(['id', 'raw', 'created_at', '_id']);
const LABELS = {
  code: 'ID del Sitio',
  municipality: 'Municipio',
  estado: 'Estado',
  categoria: 'Categoría',
  tipo_site: 'Tipo de site',
  prioridad: 'Prioridad',
  host: 'Host / Cliente',
  address: 'Dirección',
  altura_edificio: 'Altura edificio',
  num_mastiles: 'Nº mástiles',
  infra_type: 'Tipo infraestructura',
  propiedad_torre: 'Propiedad torre',
  fecha_rfi: 'Fecha RFI',
  fecha_instalacion: 'Fecha instalación tenant',
  clasificacion: 'Clasificación A&I',
  lat: 'Latitud',
  lng: 'Longitud',
};

const SiteMasterModal = ({ code, lat, lng, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Capture the previous-visit timestamp BEFORE we overwrite it with pushRecentSite.
  const [lastVisit, setLastVisit] = useState(() => {
    const prev = getRecentSites().find((s) => s.code === code);
    return prev?.at ?? null;
  });

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(null);
    const params = {};
    if (lat != null) params.lat = lat;
    if (lng != null) params.lng = lng;
    api.get(`/sites-master/${encodeURIComponent(code)}`, { params })
      .then((r) => {
        if (!cancel) {
          setData(r.data);
          const resolvedCode = r.data?.code || code;
          // Read previous visit for THIS resolved code (may differ if fallback matched by proximity)
          if (!lastVisit || (r.data?.code && r.data.code !== code)) {
            const prev = getRecentSites().find((s) => s.code === resolvedCode);
            setLastVisit(prev?.at ?? null);
          }
          pushRecentSite({
            code: resolvedCode,
            title: r.data?.name || r.data?.code || code,
            subtitle: [r.data?.municipality, r.data?.province, r.data?.host].filter(Boolean).join(' · '),
            lat: r.data?.lat ?? lat, lng: r.data?.lng ?? lng,
          });
        }
      })
      .catch((e) => { if (!cancel) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [code, lat, lng]);

  const primary = data ? Object.entries(data).filter(([k]) => !HIDDEN_KEYS.has(k) && k !== 'raw' && LABELS[k]) : [];
  const rawFields = data?.raw ? Object.entries(data.raw).filter(([, v]) => v && v !== '-' && v !== 'false') : [];
  const mapsUrl = data?.lat && data?.lng ? `https://www.google.com/maps?q=${data.lat},${data.lng}` : null;
  const sv = data?.lat && data?.lng ? `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${data.lat},${data.lng}` : null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="site-master-modal"
        className="bg-[#0a0a0a] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-3 sticky top-0 bg-[#0a0a0a]/95 backdrop-blur z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">SITE · FICHA COMPLETA</div>
              {data?._match_type && data._match_type !== 'exact' && (
                <span className="text-[9px] font-bold uppercase tracking-wide bg-purple-500/20 text-purple-300 border border-purple-500/30 px-1.5 py-0.5 rounded-full"
                  title={data._match_type === 'proximity' ? 'Ficha encontrada por proximidad geográfica' : data._match_type === 'sedra' ? 'Encontrada vía código Sedra' : 'Match por prefijo'}>
                  {data._match_type === 'proximity' ? '📍 CERCANO' : data._match_type === 'sedra' ? 'SEDRA' : 'PREFIJO'}
                </span>
              )}
              {lastVisit && (
                <span data-testid="site-master-last-visit"
                  title={new Date(lastVisit).toLocaleString('es-ES')}
                  className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide bg-white/5 text-white/70 border border-white/15 px-1.5 py-0.5 rounded-full">
                  <History size={9}/> Última visita · {relativeTime(lastVisit)}
                </span>
              )}
              {!lastVisit && data && (
                <span data-testid="site-master-first-visit"
                  className="text-[9px] font-bold uppercase tracking-wide bg-[#A3FF12]/10 text-[#A3FF12] border border-[#A3FF12]/30 px-1.5 py-0.5 rounded-full">
                  ⚡ 1.ª visita
                </span>
              )}
            </div>
            <h3 className="text-xl font-black mt-1 truncate">
              {data?.code || code}
              {data && data.code !== code && <span className="text-white/40 text-sm ml-2">({code})</span>}
            </h3>
            {data?.address && <p className="text-white/50 text-xs mt-1 truncate"><MapPin size={11} className="inline"/> {data.address}</p>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {data?.lat && data?.lng && (
              <SARButton lat={data.lat} lng={data.lng} address={data.address}
                height_m={data.height_m || null}
                site_id={data.id || null} site_name={data.name || data.code || ''}
                afecciones_report={data.afecciones_report || null}
                variant="primary"
                className="hidden sm:inline-flex"/>
            )}
            <button onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="close-site-modal"><X size={22}/></button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-16"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-orange-400 text-sm font-bold">Sin datos maestros para este site</p>
            <p className="text-white/50 text-xs mt-2">
              El código <code className="text-[#A3FF12]">{code}</code> no está en la base maestra y no hay ninguna torre a menos de 150 m.
            </p>
            <p className="text-white/40 text-xs mt-2">Los nodos KMZ (AND*, MAD*, BCN*…) son sectores de operador. Sólo las torres físicas (SPA-*) tienen ficha completa. Prueba activando la capa 🗼 Torres.</p>
          </div>
        ) : data && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Primary fields */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {primary.map(([k, v]) => (
                v ? (
                  <div key={k} className="bg-black/30 border border-white/5 rounded-lg p-2">
                    <div className="text-[10px] uppercase text-white/40">{LABELS[k]}</div>
                    <div className="text-sm text-white font-bold truncate">{String(v)}</div>
                  </div>
                ) : null
              ))}
            </div>

            {/* SAR — botón grande y siempre visible */}
            {data?.lat && data?.lng && (
              <div className="bg-gradient-to-br from-[#A3FF12]/10 to-transparent border border-[#A3FF12]/30 rounded-2xl p-4 flex items-center gap-3" data-testid="sar-section">
                <div className="flex-1">
                  <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">INFORME</div>
                  <div className="text-white font-black text-base mt-0.5">Generar SAR</div>
                  <div className="text-white/50 text-xs mt-0.5">Site Acquisition Report completo con Adquiriam + BIC + fotos</div>
                </div>
                <SARButton lat={data.lat} lng={data.lng} address={data.address}
                  height_m={data.height_m || null}
                  site_id={data.id || null} site_name={data.name || data.code || ''}
                  afecciones_report={data.afecciones_report || null}
                  variant="primary"/>
              </div>
            )}

            {/* Estado del site + enlaces externos */}
            {data?.code && <SiteStatePanel siteCode={data.code} initialState={data.state} initialHistory={data.state_history} siteInfo={data}/>}

            <div className="flex gap-2 flex-wrap items-center">
              {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12] px-3 py-1.5 rounded-full font-bold hover:bg-[#A3FF12]/20"><ExternalLink size={12}/> Google Maps</a>}
              {sv && <a href={sv} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs bg-white/5 border border-white/10 text-white px-3 py-1.5 rounded-full font-bold hover:bg-white/10"><ExternalLink size={12}/> Street View</a>}
            </div>

            {/* Site projects (documents & folders) */}
            {(data?.id || data?.code) && <SiteProjectsPanel siteId={data.id || data.code} siteInfo={data}/>}

            {/* All 58 raw fields */}
            {rawFields.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 size={14} className="text-[#A3FF12]"/>
                  <h4 className="text-sm font-black uppercase tracking-wide text-white/60">Todos los datos ({rawFields.length})</h4>
                </div>
                <div className="grid sm:grid-cols-2 gap-1 text-xs">
                  {rawFields.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2 py-1.5 px-2 bg-black/20 rounded border border-white/5">
                      <span className="text-white/50 truncate flex-shrink-0" title={k}>{k}</span>
                      <span className="text-white/90 text-right truncate min-w-0" title={String(v)}>{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteMasterModal;
